import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import {
    incrementRecurringSessionCount,
    shouldShowRecurringLimitWarning,
    shouldSuppressRecurringModal,
    shouldSuppressSoldOutRecurringModal,
} from 'features/shop/recurringModals';
import useGlobalStore from 'hooks/useGlobalStore';
import { trackEvent } from 'utils/analyticsUtils';

import ProductCardPresentation from './ProductCardPresentation';
import { useProductCardContext } from './ProductCardProvider';
import QuantityStepper from './QuantityStepper';
import RecurringToggle from './RecurringToggle';
import { PRODUCT_DATA_PROP_TYPE, PRODUCT_META_DATA_PROP_TYPE } from './types';
import { getPerksData } from './utils';

/**
 * ProductCard component that must be rendered within a ProductCardProvider
 *
 * @param {Object} props
 * @param {import('./types').ProductData} props.data - Product data
 * @param {'horizontal'|'vertical'} [props.layout=vertical] - Card layout orientation
 * @param {boolean} [props.lazyLoadImage] - Whether to lazy load the product image
 * @param {import('./types').ProductMetaData} [props.metaData={}] - Additional metadata for the product
 * @param {Function} [props.navigateToPDPOverride] - Override function for PDP navigation. When provided, this function is called instead of the default navigation behavior, bypassing router navigation and callbacks.
 * @param {Function} [props.onNavigateToPDP] - Callback function called when navigating to product detail page
 * @param {Function} [props.setModalOpen] - Callback to handle modal state
 * @param {boolean} [props.showRecurringToggle=false] - Whether to show the toggle for adding a product to a user's recurring items list.
 * @param {'PERK'|'PERK-REDEEMED'|'DEFAULT'} [props.variant='DEFAULT'] - The product card variant to display
 * @returns {JSX.Element} Product card component
 */
function ProductCard({
    data: productData,
    dsSourceOverride,
    layout,
    lazyLoadImage,
    metaData,
    navigateToPDPOverride,
    onNavigateToPDP,
    setModalOpen,
    showRecurringToggle,
    variant = 'DEFAULT',
}) {
    const {
        ageIsUnder21,
        getSelection,
        isDeliveredByImperfect,
        isRecurring,
        isRecurringEngagementFlagOn,
        loyaltyPointsAvailable,
        loyaltyPointsBalance,
        plusMembershipEligibleOrder,
        plusMembershipShortBranding,
        prefCenterEligible,
        subscriptionVariant,
        toggleRecurring,
    } = useProductCardContext();

    const {
        productsModalDataRef,
        setRecurringLimitWarningModal,
        setRecurringModal,
        setRecurringToast,
        setSoldOutRecurringModal,
    } = useGlobalStore();

    const showMembershipPrice = plusMembershipEligibleOrder && productData?.plusMemberPriceEligible;
    const showMembershipPreviewPrice = !plusMembershipEligibleOrder && productData?.plusMemberPriceEligible;

    const displayColdPacked = productData?.coldPacked && !isDeliveredByImperfect;
    const displayFrozen = productData?.frozen && isDeliveredByImperfect;

    const perksData = getPerksData({
        variant,
        loyaltyPointsBalance,
        loyaltyPointsAvailable,
        showMembershipPrice,
        productData,
    });

    const selection = getSelection(productData.id, perksData.isPerk);

    const navigate = useNavigate();

    const navigateToPDP = () => {
        if (navigateToPDPOverride) {
            navigateToPDPOverride();
            return;
        }

        // Record PDP navigation from ProductCards in a Products Modal
        // We'll use this data to optionally scroll the product into view when the modal is opened again
        if (metaData.isCarouselModalCard && productsModalDataRef.current) {
            productsModalDataRef.current.productID = productData?.id;
        }

        const baseUrl = `/shop/product/${productData.id}`;
        const url = perksData.isPerk ? `${baseUrl}?perk=true` : baseUrl;

        navigate(url, {
            state: { productData },
        });

        onNavigateToPDP?.();
    };

    const handleToggleRecurring = (checked) => {
        const { externalSku, name, soldOut } = productData;

        const showToast = (action) => {
            setRecurringToast({ open: true, itemName: name, action, timestamp: Date.now() });
        };

        const checkRecurringLimitWarning = () => {
            if (shouldShowRecurringLimitWarning()) {
                setRecurringLimitWarningModal(true);
                trackEvent('recurring warning modal opened', {
                    path: window.location.pathname,
                });
            }
        };

        const addToRecurring = () => {
            toggleRecurring(externalSku, true, { productName: name, soldOut });
            incrementRecurringSessionCount();
            showToast('added');
            checkRecurringLimitWarning();
        };

        if (!checked) {
            toggleRecurring(externalSku, false, { productName: name, soldOut });
            showToast('removed');
            return;
        }

        if (soldOut) {
            if (shouldSuppressSoldOutRecurringModal()) {
                addToRecurring();
            } else {
                setSoldOutRecurringModal({ open: true, itemName: name, externalSku, onConfirm: addToRecurring });
            }
            return;
        }

        toggleRecurring(externalSku, true, { productName: name, soldOut });
        incrementRecurringSessionCount();

        // Check if limit warning will be shown - if so, skip the recurring modal and show toast instead
        const willShowLimitWarning = shouldShowRecurringLimitWarning();

        if (shouldSuppressRecurringModal() || willShowLimitWarning) {
            showToast('added');
        } else {
            setRecurringModal({ open: true, itemName: name });
            trackEvent('recurring modal opened', {
                path: window.location.pathname,
            });
        }

        if (willShowLimitWarning) {
            setRecurringLimitWarningModal(true);
            trackEvent('recurring warning modal opened', {
                path: window.location.pathname,
            });
        }
    };

    return (
        <ProductCardPresentation
            displayColdPacked={displayColdPacked}
            displayFrozen={displayFrozen}
            isPerk={perksData.isPerk}
            isPerkRedeemable={perksData.isPerkRedeemable}
            pointPrice={perksData.pointPrice}
            productData={productData}
            layout={layout}
            lazyLoadImage={lazyLoadImage}
            navigateToPDP={navigateToPDP}
            showMembershipPrice={showMembershipPrice}
            showMembershipPreviewPrice={showMembershipPreviewPrice}
            plusMembershipShortBranding={plusMembershipShortBranding}
        >
            {!(isRecurringEngagementFlagOn && productData.soldOut) && (
                <div className="shrink-0">
                    <QuantityStepper
                        dsSource={dsSourceOverride}
                        isPerk={perksData.isPerk}
                        isPerkRedeemable={perksData.isPerkRedeemable}
                        pointPrice={perksData.pointPrice}
                        productSelectionData={selection}
                        productData={productData}
                        metaData={metaData}
                        setModalOpen={setModalOpen}
                        wineIneligible={ageIsUnder21}
                    />
                </div>
            )}
            {isRecurringEngagementFlagOn &&
                showRecurringToggle &&
                (subscriptionVariant === 'autoship' || prefCenterEligible) && (
                    <RecurringToggle
                        checked={isRecurring(productData.externalSku)}
                        onCheckedChange={handleToggleRecurring}
                        styles={{ root: 'mr-5' }}
                    />
                )}
        </ProductCardPresentation>
    );
}

ProductCard.propTypes = {
    data: PRODUCT_DATA_PROP_TYPE.isRequired,
    dsSourceOverride: PropTypes.string,
    layout: PropTypes.oneOf(['horizontal', 'vertical']),
    metaData: PRODUCT_META_DATA_PROP_TYPE,
    navigateToPDPOverride: PropTypes.func,
    onNavigateToPDP: PropTypes.func,
    setModalOpen: PropTypes.func,
};

ProductCard.defaultProps = {
    dsSourceOverride: null,
    layout: 'vertical',
    metaData: {},
    navigateToPDPOverride: undefined,
    onNavigateToPDP: undefined,
    setModalOpen: () => {},
};

export default ProductCard;
